import {
  JapanArea,
  JapanAttraction,
  JapanHotel,
  RegionListQuery,
} from "@/types/generated";
import type {
  Attraction,
  Attractions,
  City,
} from "../hotel-details/location-section/LocationMapView.types";
import { getAreaHotelThumbnailUrl } from "@app/[locale]/tourist-attraction/[city_slug]/[slug]/util";
import { generateRandomKey } from "@/utils";

const cryptoObj = globalThis.crypto;
const array = new Uint32Array(1);
cryptoObj.getRandomValues(array);

export const mapAreasToDisplayData = (
  source: JapanArea[],
  japanArea?: JapanArea[],
  regionData?: RegionListQuery,
): Attractions[] => {
  const dataArrangeByArea = (source || []).map((area: JapanArea) => {
    const currentRegion = (
      regionData?.datasourceJapans?.[0]?.region_datasource?.regions || []
    ).find((item) =>
      item?.cities_order_on_top_page?.some(
        (item: { documentId: string }) => item.documentId === area.documentId,
      ),
    );
    const currentRankInRegion = currentRegion
      ? currentRegion?.cities_order_on_top_page?.find(
          (item: { documentId: string; order: number }) =>
            item.documentId === area.documentId,
        )
      : null;
    const currentRankRegion = (
      regionData?.datasourceJapans?.[0]?.region_datasource?.selected_regions ||
      []
    ).find(
      (item: { documentId: string }) =>
        item.documentId === currentRegion?.documentId,
    );
    const cityData: City = {
      key:
        area.documentId ||
        area.item_name ||
        generateRandomKey("destination-city"),
      map_image: area.cover_image || undefined,
      title: area.area_name || "Unknown Area",
      lng: Number(area.longitude) || 0,
      lat: Number(area.latitude) || 0,
      zoom: area.map_zoom_ratio ? Number(area.map_zoom_ratio) : 12,
      orderByRegion: currentRankRegion ? currentRankRegion?.order : null,
      orderByPrefecture: currentRankInRegion ? currentRankInRegion.order : null,
    };
    const attractionsForMap: Attraction[] = (area.attractions || [])
      .filter((attr): attr is JapanAttraction => !!attr)
      .map((attr: JapanAttraction) => {
        const overviewImage = attr.properties?.overview_image?.[0];
        const imageUrl = overviewImage?.cloudflare_url || overviewImage?.url || undefined;
        return {
          key: attr.documentId || generateRandomKey("destination-attr"),
          banner_image: imageUrl,
          name: attr.properties?.name || "Unnamed Attraction",
          city: area.area_name || "Unknown Area",
          lng: Number(attr.properties?.longitude) || 0,
          lat: Number(attr.properties?.latitude) || 0,
          slug: attr?.slug ?? "",
        };
      });
    const hotelsForMap: Attraction[] = (area.hotels || [])
      .filter((hotel): hotel is JapanHotel => !!hotel)
      .map((hotel: JapanHotel) => {
        const areaHotel = japanArea
          ?.flatMap((area) => area?.hotels)
          .find((data) => data?.documentId === hotel?.documentId);
        const hotelDataImg = getAreaHotelThumbnailUrl(areaHotel);
        const hotelImg = getAreaHotelThumbnailUrl(hotel);
        const imageUrl = hotelImg?.cloudflare_url || hotelImg?.url || hotelDataImg?.cloudflare_url || hotelDataImg?.url || undefined;
        return {
          key: hotel.documentId || generateRandomKey("destination-hotel"),
          banner_image: imageUrl,
          name: hotel.properties?.hotel_name || "Unnamed Hotel",
          city: area.area_name || "Unknown Area",
          lng: Number(hotel.properties?.longitude) || 0,
          lat: Number(hotel.properties?.latitude) || 0,
          slug: hotel?.slug ?? "",
          hotelSlug: hotel?.slug ?? "",
          brandDocId: hotel?.properties?.brand?.[0]?.documentId,
          externalUrl: hotel?.redirect?.external_url || "",
        };
      });

    return {
      city: cityData,
      attractions: attractionsForMap,
      hotels: hotelsForMap,
    };
  });
  if (regionData) {
    return dataArrangeByArea
      .filter(
        (item) =>
          item.city.orderByRegion != null &&
          item.city.orderByPrefecture != null,
      )
      .sort((a, b) => {
        const regionDiff = a.city.orderByRegion! - b.city.orderByRegion!;
        if (regionDiff !== 0) return regionDiff;
        return a.city.orderByPrefecture! - b.city.orderByPrefecture!;
      });
  } else {
    return dataArrangeByArea;
  }
};
